import { createScanSummary, ScanResult, ScanSummary } from './types';
import { 
  getShuffledSolutions, 
  getWordForDateShuffled, 
  verifyPRNGConsistency,
  WORMBLE_SEED_VALUE 
} from '@/utils/wordlistEngine';

// Simple hash for verification
function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(16).padStart(8, '0');
}

export interface DeterministicScanDetails {
  seedHex: string;
  epochDate: string;
  daysSinceEpoch: number;
  computedIndex: number;
  computedWord: string;
  shuffleHash: string;
  archiveTestedDays: number;
  archiveMismatches: string[];
  futureTestedDays: number;
  futureMismatches: string[];
  timezoneTestedZones: number;
  timezoneMismatches: string[];
  duration: number;
}

export function runDeterministicPuzzleScan(autoFix: boolean = false): ScanSummary & { details?: DeterministicScanDetails } {
  const startTime = performance.now();
  const results: ScanResult[] = [];
  
  console.group('🔍 Daily Word Consistency Scan');
  console.log('Verifying deterministic puzzle generation across all clients...\n');

  // Expected seed value
  const EXPECTED_SEED = 0x574F524D;
  const EXPECTED_SEED_HEX = '0x574F524D';
  
  // Check 1: WORMBLE_SEED consistency
  if (WORMBLE_SEED_VALUE === EXPECTED_SEED) {
    results.push({
      name: 'Seed Consistency',
      status: 'pass',
      message: `Seed matches expected value: ${EXPECTED_SEED_HEX}`
    });
    console.log('✅ Seed Consistency: PASS');
  } else {
    results.push({
      name: 'Seed Consistency',
      status: 'fail',
      message: `Seed mismatch! Expected ${EXPECTED_SEED_HEX}, got different value`,
      details: 'This will cause different puzzle words across clients!'
    });
    console.error('❌ Seed Consistency: FAIL - Seed drift detected');
  }

  // Check 2: PRNG reproducibility
  const prngConsistent = verifyPRNGConsistency();
  if (prngConsistent) {
    results.push({
      name: 'PRNG Reproducibility',
      status: 'pass',
      message: 'PRNG produces consistent sequences with same seed'
    });
    console.log('✅ PRNG Reproducibility: PASS');
  } else {
    results.push({
      name: 'PRNG Reproducibility',
      status: 'fail',
      message: 'PRNG produces inconsistent results!',
      details: 'Same seed generates different random sequences'
    });
    console.error('❌ PRNG Reproducibility: FAIL');
  }

  // Check 3: Shuffle determinism
  const shuffle1 = getShuffledSolutions();
  const shuffle2 = getShuffledSolutions();
  const shufflesMatch = shuffle1.length === shuffle2.length && 
    shuffle1.every((word, i) => word === shuffle2[i]);
  
  if (shufflesMatch) {
    results.push({
      name: 'Shuffle Determinism',
      status: 'pass',
      message: `getShuffledSolutions() returns identical arrays (${shuffle1.length} words)`
    });
    console.log('✅ Shuffle Determinism: PASS');
  } else {
    results.push({
      name: 'Shuffle Determinism',
      status: 'fail',
      message: 'getShuffledSolutions() returns different arrays on subsequent calls!',
      details: 'This breaks deterministic puzzle generation'
    });
    console.error('❌ Shuffle Determinism: FAIL');
  }

  // Check 4: Shuffle hash verification
  const first50 = shuffle1.slice(0, 50).join('');
  const shuffleHash = simpleHash(first50);
  
  results.push({
    name: 'Shuffle Hash',
    status: 'info',
    message: `First 50 words hash: ${shuffleHash}`,
    details: `Compare this hash across clients/sessions. Matching hash = identical shuffle order.`
  });
  console.log(`ℹ️  Shuffle Hash: ${shuffleHash}`);

  // Check 5: Archive date mapping - sample every 7th day for performance
  const epoch = new Date(2024, 0, 1);
  const today = new Date();
  const totalDays = Math.floor((today.getTime() - epoch.getTime()) / (1000 * 60 * 60 * 24));
  const archiveSampleRate = 7; // Test every 7th day
  const archiveTestedDays = Math.ceil(totalDays / archiveSampleRate);
  const archiveMismatches: string[] = [];
  const archiveWords = new Set<string>();
  
  for (let day = 0; day < totalDays; day += archiveSampleRate) {
    const date = new Date(epoch);
    date.setDate(date.getDate() + day);
    const word = getWordForDateShuffled(date);
    
    // Check for unexpected duplicates (shouldn't happen within same cycle)
    if (day < shuffle1.length && archiveWords.has(word)) {
      archiveMismatches.push(`Day ${day}: Duplicate word ${word}`);
    }
    archiveWords.add(word);
  }
  
  if (archiveMismatches.length === 0) {
    results.push({
      name: 'Archive Date Mapping',
      status: 'pass',
      message: `Tested ${archiveTestedDays} days from epoch to today - no issues`,
      details: `Sampling rate: every ${archiveSampleRate}th day`
    });
    console.log(`✅ Archive Date Mapping: PASS (${archiveTestedDays} days tested)`);
  } else {
    results.push({
      name: 'Archive Date Mapping',
      status: 'fail',
      message: `Found ${archiveMismatches.length} issues in archive mapping`,
      details: archiveMismatches.slice(0, 5).join('; ')
    });
    console.error(`❌ Archive Date Mapping: FAIL - ${archiveMismatches.length} issues`);
  }

  // Check 6: Future date mapping - sample every 30th day for next 365 days
  const futureSampleRate = 30; // Test every 30th day
  const futureDays = 365;
  const futureTestedDays = Math.ceil(futureDays / futureSampleRate);
  const futureMismatches: string[] = [];
  
  for (let day = 1; day <= futureDays; day += futureSampleRate) {
    const date = new Date(today);
    date.setDate(date.getDate() + day);
    
    try {
      const word = getWordForDateShuffled(date);
      if (!word || word.length !== 5 || !/^[A-Z]+$/.test(word)) {
        futureMismatches.push(`Day +${day}: Invalid word format "${word}"`);
      }
    } catch (e) {
      futureMismatches.push(`Day +${day}: Error computing word - ${e}`);
    }
  }
  
  if (futureMismatches.length === 0) {
    results.push({
      name: 'Future Date Mapping',
      status: 'pass',
      message: `Tested ${futureTestedDays} future dates (next 365 days) - no issues`,
      details: `Sampling rate: every ${futureSampleRate}th day`
    });
    console.log(`✅ Future Date Mapping: PASS (${futureTestedDays} days tested)`);
  } else {
    results.push({
      name: 'Future Date Mapping',
      status: 'fail',
      message: `Found ${futureMismatches.length} issues in future mapping`,
      details: futureMismatches.slice(0, 5).join('; ')
    });
    console.error(`❌ Future Date Mapping: FAIL - ${futureMismatches.length} issues`);
  }

  // Check 7: Timezone safety - verify same calendar date at different times returns same word
  const baseDate = new Date(2024, 5, 15); // June 15, 2024 (local midnight)
  const baseWord = getWordForDateShuffled(baseDate);
  const timezoneMismatches: string[] = [];
  
  // Test various times throughout the same calendar day
  const timesToTest = [
    { hours: 0, minutes: 0 },   // midnight
    { hours: 6, minutes: 30 },  // early morning
    { hours: 12, minutes: 0 },  // noon
    { hours: 18, minutes: 45 }, // evening
    { hours: 23, minutes: 59 }, // near midnight
  ];
  
  for (const time of timesToTest) {
    const testDate = new Date(2024, 5, 15, time.hours, time.minutes);
    const word = getWordForDateShuffled(testDate);
    if (word !== baseWord) {
      timezoneMismatches.push(`Time ${time.hours}:${time.minutes}: Expected ${baseWord}, got ${word}`);
    }
  }
  
  // Verify adjacent dates return DIFFERENT words (boundary test)
  const dayBefore = new Date(2024, 5, 14);
  const dayAfter = new Date(2024, 5, 16);
  const wordBefore = getWordForDateShuffled(dayBefore);
  const wordAfter = getWordForDateShuffled(dayAfter);
  
  if (wordBefore === baseWord) {
    timezoneMismatches.push('June 14 returned same word as June 15 - date boundary issue!');
  }
  if (wordAfter === baseWord) {
    timezoneMismatches.push('June 16 returned same word as June 15 - date boundary issue!');
  }
  
  const timezoneTestedZones = timesToTest.length + 2; // times + boundary tests
  
  if (timezoneMismatches.length === 0) {
    results.push({
      name: 'Timezone Safety',
      status: 'pass',
      message: 'Same calendar date at different times returns same word',
      details: 'Date boundaries also verified'
    });
    console.log(`✅ Timezone Safety: PASS (${timezoneTestedZones} time tests)`);
  } else {
    results.push({
      name: 'Timezone Safety',
      status: 'fail',
      message: `Found ${timezoneMismatches.length} time-of-day inconsistencies!`,
      details: timezoneMismatches.slice(0, 3).join('; ')
    });
    console.error(`❌ Timezone Safety: FAIL - ${timezoneMismatches.length} mismatches`);
  }

  // Check 8: Seed drift detection (localStorage)
  let seedDrift = false;
  const storedSeed = localStorage.getItem('wormble-puzzle-seed');
  if (storedSeed && storedSeed !== EXPECTED_SEED_HEX) {
    seedDrift = true;
    results.push({
      name: 'Seed Drift',
      status: 'warn',
      message: 'localStorage has different seed than hardcoded constant',
      details: `Stored: ${storedSeed}, Expected: ${EXPECTED_SEED_HEX}`,
      autoFixed: autoFix
    });
    console.warn(`⚠️  Seed Drift: localStorage seed mismatch`);
    
    if (autoFix) {
      localStorage.removeItem('wormble-puzzle-seed');
      console.log('🔧 Auto-fixed: Cleared localStorage seed');
    }
  } else {
    results.push({
      name: 'Seed Drift',
      status: 'pass',
      message: 'No seed drift detected'
    });
    console.log('✅ Seed Drift: PASS');
  }

  // Check 9: Off-by-one errors
  const epochWord = getWordForDateShuffled(epoch);
  const dayAfterEpoch = new Date(2024, 0, 2);
  const dayAfterWord = getWordForDateShuffled(dayAfterEpoch);
  const offByOneErrors = epochWord === dayAfterWord;
  
  if (!offByOneErrors) {
    results.push({
      name: 'Off-by-One Errors',
      status: 'pass',
      message: 'Epoch (2024-01-01) and day-after have different words',
      details: 'Date math is correct'
    });
    console.log('✅ Off-by-One Errors: PASS');
  } else {
    results.push({
      name: 'Off-by-One Errors',
      status: 'fail',
      message: 'Epoch and day-after return same word!',
      details: 'Indicates date calculation bug'
    });
    console.error('❌ Off-by-One Errors: FAIL');
  }

  // Check 10: URL parameter override detection
  const urlParams = new URLSearchParams(window.location.search);
  const puzzleParam = urlParams.get('puzzle') || urlParams.get('p');
  
  if (puzzleParam) {
    results.push({
      name: 'URL Parameter Override',
      status: 'info',
      message: `Detected puzzle parameter: ${puzzleParam}`,
      details: 'Verify this parameter correctly overrides date-based selection'
    });
    console.log(`ℹ️  URL Parameter Override: Detected ?puzzle=${puzzleParam}`);
  } else {
    results.push({
      name: 'URL Parameter Override',
      status: 'pass',
      message: 'No URL parameter override detected'
    });
    console.log('✅ URL Parameter Override: Not active');
  }

  // Check 11: Friend mode consistency
  const friendParam = urlParams.get('friend');
  if (friendParam) {
    results.push({
      name: 'Friend Mode',
      status: 'info',
      message: `Friend mode active: ${friendParam}`,
      details: 'Verify friend mode uses same word selection logic'
    });
    console.log(`ℹ️  Friend Mode: Active (${friendParam})`);
  } else {
    results.push({
      name: 'Friend Mode',
      status: 'pass',
      message: 'Friend mode not active'
    });
    console.log('✅ Friend Mode: Not active');
  }

  const endTime = performance.now();
  const duration = Math.round(endTime - startTime);

  // Get today's word info for details
  const todayInfo = {
    seedHex: EXPECTED_SEED_HEX,
    epochDate: '2024-01-01',
    daysSinceEpoch: totalDays,
    computedIndex: totalDays % shuffle1.length,
    computedWord: getWordForDateShuffled(today),
    shuffleHash,
    archiveTestedDays,
    archiveMismatches,
    futureTestedDays,
    futureMismatches,
    timezoneTestedZones,
    timezoneMismatches,
    duration
  };

  console.log(`\n⏱️  Scan completed in ${duration}ms`);
  console.groupEnd();

  const summary = createScanSummary('Daily Word Consistency', results);
  
  return {
    ...summary,
    details: todayInfo
  };
}
